function _defineProperty(obj, key, value) { key = _toPropertyKey(key); if (key in obj) { Object.defineProperty(obj, key, { value: value, enumerable: true, configurable: true, writable: true }); } else { obj[key] = value; } return obj; }
function _toPropertyKey(arg) { var key = _toPrimitive(arg, "string"); return typeof key === "symbol" ? key : String(key); }
function _toPrimitive(input, hint) { if (typeof input !== "object" || input === null) return input; var prim = input[Symbol.toPrimitive]; if (prim !== undefined) { var res = prim.call(input, hint || "default"); if (typeof res !== "object") return res; throw new TypeError("@@toPrimitive must return a primitive value."); } return (hint === "string" ? String : Number)(input); }
import { isNone } from '@ember/utils';
import { CurrencyCode } from '@square/dashboard-protos/models/squareup/common/currency_codes';
import { Currency as ConnectCurrency, Money as ConnectMoney } from '@square/dashboard-protos/models/squareup/connect/v2/common/money';
import { Money as CurrencyMoney } from '@square/dashboard-protos/models/squareup/common/currency';
import { getFormat } from '@square/dashboard-utilities/utils/i18n-deprecated/format';
import getCashRounding from '../utils/cash-rounding';
export default class Money {
  get isNotZero() {
    return Boolean(this.amount);
  }
  get isZero() {
    return !this.isNotZero;
  }
  get formatted() {
    return Money.formatCents(this.amount, this.currencyCode);
  }
  get formattedRounded() {
    return Money.formatRounded(this.amount, this.currencyCode);
  }
  get formattedShort() {
    if (isNone(this.amount)) {
      return this.formattedRounded;
    }
    if (this.amount % 100 !== 0) {
      return this.formatted;
    } else {
      return this.formattedRounded;
    }
  }
  get negated() {
    return Money.fromCents(isNone(this.amount) ? null : -this.amount, this.currencyCode);
  }
  get unsigned() {
    return Money.fromCents(isNone(this.amount) ? null : Math.abs(this.amount), this.currencyCode);
  }
  get isCashRoundingRequired() {
    const roundingUnit = getCashRounding(this.currencyCode);
    if (isNone(this.amount)) {
      return false;
    }
    return this.amount % roundingUnit !== 0;
  }
  cashRound() {
    if (isNone(this.amount)) {
      return Money.fromCents(null, this.currencyCode);
    }
    const roundingUnit = getCashRounding(this.currencyCode);
    return Money.fromCents(Math.round(this.amount / roundingUnit) * roundingUnit, this.currencyCode);
  }
  add(other) {
    if (this.currencyCode !== other.currencyCode) {
      throw new Error(`Cannot add currencies: ${this.currencyCode} + ${other.currencyCode}`);
    }
    const _amount = this.amount ?? 0;
    const otherAmount = other.amount;
    const _otherAmount = otherAmount ?? 0;
    return Money.fromCents(_amount + _otherAmount, this.currencyCode);
  }
  subtract(other) {
    if (this.currencyCode !== other.currencyCode) {
      throw new Error(`Cannot subtract currencies: ${this.currencyCode} + ${other.currencyCode}`);
    }
    const _amount = this.amount ?? 0;
    const otherAmount = other.amount;
    const _otherAmount = otherAmount ?? 0;
    return Money.fromCents(_amount - _otherAmount, this.currencyCode);
  }
  multiply(factor) {
    const _amount = this.amount ?? 0;
    return Money.fromCents(_amount * factor, this.currencyCode);
  }

  // NOTE: Javascript division use wisely
  divide(factor) {
    const _amount = this.amount ?? 0;
    return Money.fromCents(_amount / factor, this.currencyCode);
  }

  // working with larger values to avoid floating point errors
  // 5% expressed as 5, not 0.05
  percentage(percentage) {
    const _amount = this.amount ?? 0;
    return Money.fromCents(_amount * percentage / 100, this.currencyCode);
  }
  isDefined() {
    return !isNone(this._amount);
  }
  equals(other) {
    if (isNone(other)) {
      return false;
    }
    return this.amount === other.amount && this.currencyCode === other.currencyCode;
  }

  // Appends my formatted value onto Ember's default toString, for ease reading test failures.
  // http://emberjs.com/api/classes/Ember.CoreObject.html#method_toString
  toStringExtension() {
    return getFormat() && this.formatted;
  }
  toProto() {
    const {
      amount,
      currencyCode
    } = this;
    const _amount = amount ?? undefined;
    return CurrencyMoney.create({
      amount: _amount,
      currencyCode: CurrencyCode[currencyCode]
    });
  }
  toConnectProto() {
    const {
      amount,
      currencyCode
    } = this;
    const _amount = amount ?? undefined;
    return ConnectMoney.create({
      amount: _amount,
      currency: ConnectCurrency[currencyCode]
    });
  }

  // todo: convert all instances of get to default js property access and then delete this function.
  get(property) {
    return this[property] ?? null;
  }
  toJSON() {
    return {
      _amount: this._amount,
      _currencyCode: this._currencyCode
    };
  }
  getAmount(_amount) {
    if (isNone(_amount)) {
      return null;
    }
    const amount = _amount;
    const cents = Math.floor(amount);
    const fraction = Math.floor(amount * 100) % 100;
    if (fraction < 50) {
      return cents;
    } else if (fraction === 50) {
      if (cents % 2 === 0) {
        return cents;
      } else {
        return cents + 1;
      }
    } else if (fraction > 50) {
      return cents + 1;
    }
    return null;
  }
  constructor(_amount, _currencyCode) {
    _defineProperty(this, "_amount", null);
    _defineProperty(this, "_currencyCode", void 0);
    _defineProperty(this, "amount", null);
    _defineProperty(this, "currencyCode", void 0);
    Object.defineProperty(this, '_amount', {
      value: _amount,
      writable: false
    });
    Object.defineProperty(this, 'amount', {
      value: this.getAmount(_amount),
      writable: false
    });
    // _currencyCode needed for legacy compatibility
    Object.defineProperty(this, '_currencyCode', {
      value: _currencyCode,
      writable: false
    });
    Object.defineProperty(this, 'currencyCode', {
      value: _currencyCode,
      writable: false
    });
  }
  static formatCents(amount, currencyCode) {
    return getFormat()?.currency(amount, currencyCode);
  }
  static formatRounded(amount, currencyCode) {
    return getFormat()?.currency(amount, currencyCode, {
      maximumFractionDigits: 0,
      minimumFractionDigits: 0
    });
  }
  static create(attrs) {
    if (isNone(attrs)) {
      throw new Error('expected attrs');
    }
    for (const key of Object.keys(attrs)) {
      if (!['amount', 'currencyCode'].includes(key)) {
        throw new Error(`unexpected key ${key}, wanted 'amount' or 'currencyCode'`);
      }
    }
    if (isNone(attrs.currencyCode)) {
      throw new Error('expected currencyCode');
    }
    return new Money(attrs.amount, attrs.currencyCode);
  }

  // Pro-tip: initializers/money sets up default currencyCode
  static zero(currencyCode) {
    const _currencyCode = isNone(currencyCode) ? this.defaultCurrencyCode : currencyCode;
    return this.fromCents(0, _currencyCode);
  }

  // Pro-tip: initializers/money sets up default currencyCode
  static fromCents(amount, currencyCode) {
    const _currencyCode = currencyCode ?? this.defaultCurrencyCode;
    return this.create({
      amount,
      currencyCode: _currencyCode
    });
  }
  static sum(values, currencyCode) {
    const _currencyCode = currencyCode ?? this.defaultCurrencyCode;
    if (values.length <= 0) {
      return this.zero(_currencyCode);
    }
    return values.reduce((previous, current) => previous.add(current), this.zero(_currencyCode || values[0].currencyCode));
  }
  static fromProto(proto) {
    if (!proto) {
      return null;
    }
    return this.create({
      amount: proto.amount,
      currencyCode: CurrencyCode[proto.currencyCode]
    });
  }
  static fromConnectProto(proto) {
    if (!proto) {
      return null;
    }
    return this.create({
      amount: proto.amount,
      currencyCode: ConnectCurrency[proto.currency]
    });
  }
  static detectInstance(obj) {
    return obj instanceof this;
  }
}
_defineProperty(Money, "defaultCurrencyCode", null);